import { Box, Chip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { systemConfigKeyEnum, wasteDirectionEnum } from '../../../../constants';
import { getWasteDirectionLabel } from '../../../../constants/labels';
import { useCreateDailyWasteMutation, useUpdateDailyWasteMutation } from '../../../../hooks/mutations/useDailyWasteMutation';
import { useLoadNaturesQuery } from '../../../../hooks/queries/useLoadNaturesQuery';
import { useQuarriesQuery } from '../../../../hooks/queries/useQuarriesQuery';
import { useSitesQuery } from '../../../../hooks/queries/useSitesQuery';
import { useTrucksCompaniesQuery } from '../../../../hooks/queries/useTrucksCompaniesQuery';
import { useWasteCodesQuery } from '../../../../hooks/queries/useWasteCodesQuery';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { DailyWaste } from '../../../../services/apiSLTP/dailyReports/dailyWastes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageValidationDailyWasteModalProps extends ModalProps {
    refetch: () => void;
    dailyWaste: DailyWaste | null;
    activities: OptionType[];
    siteName?: string;
}

const ManageValidationDailyWasteModal: React.FC<ManageValidationDailyWasteModalProps> = ({
    open,
    handleClose,
    activities,
    refetch,
    dailyWaste,
    siteName,
}) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();

    const [selectedActivity, setSelectedActivity] = useState<OptionType>({
        label: activities.find((activity) => activity.value === dailyWaste?.activityId)?.label || '',
        value: dailyWaste?.activityId || '',
    });
    const [currentWasteId, setCurrentWasteId] = useState(dailyWaste?._id || '');
    const [loadNature, setLoadNature] = useState<OptionType | null>(null);
    const [wasteCode, setWasteCode] = useState<OptionType | null>(null);
    const [location, setLocation] = useState<OptionType | null>(null);
    const [company, setCompany] = useState<OptionType | null>(null);
    const [direction, setDirection] = useState<OptionType | null>(
        dailyWaste?.direction
            ? { label: capitalizeFirstLetter(t(getWasteDirectionLabel(dailyWaste.direction))), value: dailyWaste.direction.toString() }
            : null
    );
    const [quantity, setQuantity] = useState(dailyWaste?.quantity?.toString() || '');
    const [unitPrice, setUnitPrice] = useState(dailyWaste?.unitPrice?.toString() || '');

    const { data: loadNatures, isLoading: isLoadNaturesLoading } = useLoadNaturesQuery({ queries: { active: true } });
    const { data: wasteCodes, isLoading: isWasteCodesLoading } = useWasteCodesQuery({ queries: { active: true } });
    const { data: sites, isLoading: isSitesLoading } = useSitesQuery({ queries: { active: true } });
    const { data: quarries, isLoading: isQuarriesLoading } = useQuarriesQuery({ queries: { active: true } });
    const { data: companies, isLoading: isCompaniesLoading } = useTrucksCompaniesQuery({ queries: { active: true } });

    const { mutate: createDailyWaste } = useCreateDailyWasteMutation({
        onSuccess: (data) => {
            refetch();
            setCurrentWasteId(data.data._id);
            handleClose();
        },
    });

    const { mutate: updateDailyWaste } = useUpdateDailyWasteMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    useEffect(() => {
        if (dailyWaste) {
            if (dailyWaste.loadNatureId) {
                setLoadNature({
                    label: dailyWaste.loadNatureId.designation,
                    value: dailyWaste.loadNatureId._id,
                });
            }
            if (dailyWaste.wasteCodeId) {
                setWasteCode({
                    label: `${dailyWaste.wasteCodeId.designation} - ${dailyWaste.wasteCodeId.description}`,
                    value: dailyWaste.wasteCodeId._id,
                });
            }
            if (dailyWaste.siteId) {
                setLocation({
                    label: dailyWaste.siteId.name,
                    value: dailyWaste.siteId._id,
                    group: capitalizeFirstLetter(t('sites')),
                });
            }
            if (dailyWaste.quarryId) {
                setLocation({
                    label: dailyWaste.quarryId.designation,
                    value: dailyWaste.quarryId._id,
                    group: capitalizeFirstLetter(t('quarries')),
                });
            }
            if (dailyWaste.companyId) {
                setCompany({
                    label: dailyWaste.companyId.designation,
                    value: dailyWaste.companyId._id,
                });
            }
        }
    }, [dailyWaste]);

    const handleCreate = async () => {
        if (!loadNature || !direction || !location || !company) return;

        createDailyWaste({
            activityId: selectedActivity.value,
            loadNatureId: loadNature.value,
            wasteCodeId: wasteCode ? wasteCode.value : undefined,
            siteId: location?.group === capitalizeFirstLetter(t('sites')) ? location.value : undefined,
            quarryId: location?.group === capitalizeFirstLetter(t('quarries')) ? location.value : undefined,
            companyId: company.value,
            direction: parseInt(direction.value),
            quantity: parseFloat(quantity),
            unitPrice: parseFloat(unitPrice),
        });
    };

    const handleUpdate = async () => {
        if (!currentWasteId || !loadNature || !direction || !location) return;

        updateDailyWaste({
            dailyWasteId: currentWasteId,
            data: {
                loadNatureId: loadNature.value,
                wasteCodeId: wasteCode ? wasteCode.value : null,
                siteId: location?.group === capitalizeFirstLetter(t('sites')) ? location.value : null,
                quarryId: location?.group === capitalizeFirstLetter(t('quarries')) ? location.value : null,
                companyId: company?.value,
                direction: parseInt(direction.value),
                quantity: parseFloat(quantity),
                unitPrice: parseFloat(unitPrice),
            },
        });
    };

    const isDangerousWaste = wasteCode?.label.endsWith('*') || false;

    return (
        <ModalBody
            title={t('waste')}
            subtitle={!!currentWasteId.length ? t('update') : t('add')}
            description={siteName}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                currentWasteId.length ? handleUpdate() : handleCreate();
            }}
            disableSubmit={
                !(
                    loadNature &&
                    ((direction?.value && parseInt(direction?.value) === wasteDirectionEnum.IN) || wasteCode) &&
                    direction &&
                    location &&
                    company
                )
            }
        >
            <DropDown
                selectedItem={selectedActivity}
                items={activities}
                handleChange={(event, value) => setSelectedActivity({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('activityOf')}
                required
            />
            <DropDown
                selectedItem={direction}
                items={Object.values(wasteDirectionEnum).map((value) => ({
                    label: capitalizeFirstLetter(t(getWasteDirectionLabel(value))),
                    value: value.toString(),
                }))}
                handleChange={(event, value) => setDirection(value)}
                inputLabel={t('direction')}
                required
            />
            <DropDown
                selectedItem={loadNature}
                items={
                    loadNatures?.map((ln) => ({
                        label: ln.designation,
                        value: ln._id,
                    })) || []
                }
                handleChange={(event, value) => setLoadNature(value)}
                inputLabel={t('loadNature')}
                required
                loading={isLoadNaturesLoading}
            />

            {direction?.value && parseInt(direction?.value) !== wasteDirectionEnum.IN && (
                <DropDown
                    selectedItem={wasteCode}
                    items={
                        wasteCodes?.map((wc) => ({
                            label: `${wc.designation} - ${wc.description}`,
                            value: wc._id,
                        })) || []
                    }
                    handleChange={(event, value) => setWasteCode(value)}
                    inputLabel={t('wasteCode')}
                    required
                    loading={isWasteCodesLoading}
                />
            )}

            {isDangerousWaste && (
                <Box sx={{ mb: 2 }}>
                    <Chip label={`⚠️ ${t('dangerousWaste')}`} color="error" size="small" />
                </Box>
            )}

            <DropDown
                selectedItem={company}
                items={
                    companies?.map((c) => ({
                        label: c.designation,
                        value: c._id,
                    })) || []
                }
                handleChange={(event, value) => {
                    setCompany(value);
                }}
                inputLabel={t('company')}
                loading={isCompaniesLoading}
                required
            />

            <DropDown
                selectedItem={location}
                items={(
                    sites?.map((s) => ({
                        label: s.name,
                        value: s._id,
                        group: capitalizeFirstLetter(t('sites')),
                    })) || []
                ).concat(
                    quarries?.map((q) => ({
                        label: q.designation,
                        value: q._id,
                        group: capitalizeFirstLetter(t('quarries')),
                    })) || []
                )}
                handleChange={(event, value) => {
                    setLocation(value);
                }}
                inputLabel={t('location')}
                loading={isSitesLoading || isQuarriesLoading}
                grouped
                required
            />

            {/*<DropDown
                selectedItem={quarry}
                items={
                    quarries?.map((q) => ({
                        label: q.designation,
                        value: q._id,
                    })) || []
                }
                handleChange={(event, value) => {
                    setQuarry(value);
                    if (value) setSite(null);
                }}
                inputLabel={t('quarry')}
                loading={isQuarriesLoading}
            />
*/}
            <CustomTextInput value={quantity} onChange={setQuantity} label={`${t('quantity')}`} startAdornment={<div>{'t'}</div>} type="number" />

            <CustomTextInput
                value={unitPrice}
                onChange={setUnitPrice}
                label={t('unitPrice')}
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
        </ModalBody>
    );
};

export default ManageValidationDailyWasteModal;
